import { useState } from "react";
import { z } from "zod";
import { PlusIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { CreateChatStreamValidation, useCreateChatStream } from "@/lib/api/chats";
import { ChatStreamForm, ChatStreamFormData } from "./ChatStreamForm";

export interface CreateChatStreamProps {
  defaultValues?: {
    name?: string;
    customerIds?: string[];
    phoneNumbers?: string[];
  };
  triggerClassName?: string;
  onSuccess?: () => void;
  isOpen?: boolean;
  onClose?: () => void;
}

export function CreateChatStream({
  defaultValues,
  triggerClassName = "gap-2 mr-2",
  onSuccess,
  isOpen,
  onClose: externalOnClose
}: CreateChatStreamProps) {
  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const createChatStream = useCreateChatStream();

  const onClose = () => {
    if (externalOnClose) {
      externalOnClose();
    } else {
      setIsModalOpen(false);
    }
  };

  const onStatusChange = (status: boolean) => {
    if (externalOnClose && !status) {
      externalOnClose();
    } else {
      setIsModalOpen(status);
    }
  };

  const modalOpen = isOpen !== undefined ? isOpen : isModalOpen;

  const handleSubmit = async (base_data: ChatStreamFormData) => {
    if (isCreating) return;
    setIsCreating(true);

    try {
      const dataResult = CreateChatStreamValidation.safeParse(base_data);
      if (!dataResult.success) {
        toast({
          title: "Invalid data",
          description: '',
        });
        return;
      }

      const data = dataResult.data;
      const result = await createChatStream(data);

      if (result.success) {
        toast({
          title: "Chat stream created",
          description: `Successfully created "${data.name}"`,
        });
        onClose();
        onSuccess?.();
      } else {
        toast({
          title: "Failed to create chat stream",
          description: result.message || "",
        });
      }
    } catch (error) {
      console.error("Failed to create chat stream", error);
      toast({
        title: "Failed to create chat stream",
        description: "",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={modalOpen} onOpenChange={onStatusChange}>
      {isOpen === undefined && (
        <DialogTrigger asChild>
          <Button variant="outline" className={triggerClassName}>
            <PlusIcon className="h-4 w-4" />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[900px] lg:max-w-[1200px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Chat Stream</DialogTitle>
          <DialogDescription>
            Create a new group chat stream with customers and phone numbers
          </DialogDescription>
        </DialogHeader>

        <ChatStreamForm
          mode="create"
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          isSubmitting={isCreating}
        >
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isCreating}>
              Cancel
            </Button>
          </DialogFooter>
        </ChatStreamForm>
      </DialogContent>
    </Dialog>
  );
}
